/**
 * api.js — the portal's only way to talk to the server.
 *
 * Contract lifted verbatim from the shipped bundle so a signed-in session
 * survives this rebuild: same localStorage key, same Bearer header, same
 * base-URL override. A client with the old portal open keeps their token.
 */

const BASE = (typeof window !== "undefined" && window.PORTAL_API_BASE) || "/api";
const TOKEN_KEY = "dleaps_portal_token";
const TIMEOUT = 20000;

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

async function request(method, path, body) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), TIMEOUT);

  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: ctl.signal,
    });
  } catch (e) {
    const err = new Error(
      e && e.name === "AbortError"
        ? "The studio server took too long to respond."
        : "Could not reach the studio server. Check your connection."
    );
    err.network = true;
    throw err;
  } finally {
    clearTimeout(timer);
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Error ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  get: (p) => request("GET", p),
  post: (p, b) => request("POST", p, b),
  put: (p, b) => request("PUT", p, b),
  del: (p) => request("DELETE", p),

  /**
   * Multipart. Deliberately NOT routed through request(): setting Content-Type
   * by hand on a FormData body omits the multipart boundary and PHP receives
   * an empty $_FILES. Let the browser set it.
   */
  upload: async (p, formData) => {
    const headers = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}${p}`, { method: "POST", headers, body: formData });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);
    return data;
  },
};

/** Fire-and-forget. Used for ad impressions, which must never block a paint
 *  and must never surface an error to a client who did not ask for one. */
export function beacon(path, body) {
  try {
    const token = getToken();
    const payload = JSON.stringify(body || {});
    if (navigator.sendBeacon && !token) {
      navigator.sendBeacon(`${BASE}${path}`, new Blob([payload], { type: "application/json" }));
      return;
    }
    fetch(`${BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch { /* never throws */ }
}
